import { useMemo } from 'react';

import { NumberUtils } from '@spotify/confidence-core-react';
import {
  MetricDirection,
  MetricKind,
} from '@spotify/plugin-confidence-metrics-react';

import { useFetchDisplayNames } from '../useFetchDisplayNames';

type MetricResult = {
  displayName?: string;
  name?: string;
};

export type MetricRole = {
  metricKind?: MetricKind;
  minimumDetectableEffect?: number;
  nonInferiorityMargin?: number;
};

export type Metric = {
  metric?: string | MetricResult;
  metricRole?: MetricRole;
  preferredDirection?: unknown;
  schedule?: string;
  surfaceOnly?: boolean;
  [key: string]: unknown;
};

export type Filter = {
  displayName?: string;
  entity?: string;
  [key: string]: unknown;
};

export type MetricsData = {
  metrics?: Metric[];
  bucket?: string;
  entity?: string;
  assignmentTable?: string;
  exposureMetric?: string;
  exposureTable?: string;
  filters?: Filter[];
  [key: string]: unknown;
};

export type MetricUpdate = {
  oldMetric: Metric;
  newMetric: Metric;
  id: string;
};

export const getMetricId = (metric?: string | MetricResult): string => {
  if (!metric) {
    return '';
  }

  if (typeof metric === 'string') {
    return metric;
  }

  return metric.name || metric.displayName || '';
};

export const isSuccessMetric = (metric: Metric): boolean => {
  return (
    metric.metricRole?.metricKind === MetricKind.SUCCESS ||
    metric.metricRole?.minimumDetectableEffect !== undefined
  );
};

export const isGuardrailMetric = (metric: Metric): boolean => {
  return metric.metricRole?.metricKind === MetricKind.GUARDRAIL;
};

export const getMetricTypeLabel = (metricRole?: MetricRole): string => {
  if (!metricRole?.metricKind) {
    return '';
  }

  switch (metricRole.metricKind) {
    case MetricKind.SUCCESS:
      return 'Success metric';
    case MetricKind.GUARDRAIL:
      return 'Guardrail metric';
    default:
      return '';
  }
};

export const formatMDE = (mde: number | undefined): string =>
  NumberUtils.formatRatioAsPercentage(mde, 1, 'Not set');

export const formatNIM = (nim: number | undefined): string =>
  NumberUtils.formatRatioAsPercentage(nim, 1, 'Not set');

export const formatDirection = (direction: unknown): string => {
  if (direction === MetricDirection.INCREASE) {
    return 'Increase';
  }
  if (direction === MetricDirection.DECREASE) {
    return 'Decrease';
  }
  return 'Any direction';
};

export const formatBucket = (bucket: unknown): string => {
  if (bucket === 'HOURS') return 'Hourly';
  if (bucket === 'DAYS') return 'Daily';
  return String(bucket);
};

const hasScheduleChanged = (oldMetric: Metric, newMetric: Metric): boolean => {
  return oldMetric.schedule !== newMetric.schedule;
};

const hasMetricChanged = (oldMetric: Metric, newMetric: Metric): boolean => {
  if (oldMetric.metricRole?.metricKind !== newMetric.metricRole?.metricKind) {
    return true;
  }

  if (isSuccessMetric(oldMetric) && isSuccessMetric(newMetric)) {
    const oldMDE = oldMetric.metricRole?.minimumDetectableEffect;
    const newMDE = newMetric.metricRole?.minimumDetectableEffect;
    if (oldMDE !== newMDE) {
      return true;
    }
  }

  if (isGuardrailMetric(oldMetric) && isGuardrailMetric(newMetric)) {
    const oldNIM = oldMetric.metricRole?.nonInferiorityMargin;
    const newNIM = newMetric.metricRole?.nonInferiorityMargin;
    if (oldNIM !== newNIM) {
      return true;
    }
  }

  if (oldMetric.preferredDirection !== newMetric.preferredDirection) {
    return true;
  }

  if (hasScheduleChanged(oldMetric, newMetric)) {
    return true;
  }

  if (oldMetric.surfaceOnly !== newMetric.surfaceOnly) {
    return true;
  }

  return false;
};

export const useMetricsChangeDiff = (
  oldValue: MetricsData | null,
  newValue: MetricsData | null,
) => {
  const oldMetrics = useMemo(
    () => oldValue?.metrics ?? [],
    [oldValue?.metrics],
  );
  const newMetrics = useMemo(
    () => newValue?.metrics ?? [],
    [newValue?.metrics],
  );

  const allMetricIds = useMemo(() => {
    const ids = [
      ...oldMetrics.map(m => getMetricId(m.metric)),
      ...newMetrics.map(m => getMetricId(m.metric)),
      oldValue?.exposureMetric,
      newValue?.exposureMetric,
    ];
    return ids.filter((id): id is string => !!id);
  }, [
    oldMetrics,
    newMetrics,
    oldValue?.exposureMetric,
    newValue?.exposureMetric,
  ]);

  const allEntityIds = useMemo(() => {
    const ids = [oldValue?.entity, newValue?.entity];
    return ids.filter((id): id is string => !!id);
  }, [oldValue?.entity, newValue?.entity]);

  const allAssignmentTableIds = useMemo(() => {
    const ids = [oldValue?.assignmentTable, newValue?.assignmentTable];
    return ids.filter((id): id is string => !!id);
  }, [oldValue?.assignmentTable, newValue?.assignmentTable]);

  useFetchDisplayNames({
    metrics: allMetricIds,
    entities: allEntityIds,
    assignmentTables: allAssignmentTableIds,
  });

  const oldMetricsById = new Map(
    oldMetrics.map(m => [getMetricId(m.metric), m]),
  );
  const newMetricIds = new Set(newMetrics.map(m => getMetricId(m.metric)));

  const addedMetrics = newMetrics.filter(
    m => !oldMetricsById.has(getMetricId(m.metric)),
  );
  const removedMetrics = oldMetrics.filter(
    m => !newMetricIds.has(getMetricId(m.metric)),
  );

  const updatedMetrics = newMetrics
    .map(newMetric => {
      const id = getMetricId(newMetric.metric);
      const oldMetric = oldMetricsById.get(id);

      if (!oldMetric || !hasMetricChanged(oldMetric, newMetric)) {
        return null;
      }

      return { oldMetric, newMetric, id };
    })
    .filter((item): item is MetricUpdate => item !== null);

  const kindChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      oldMetric.metricRole?.metricKind !== newMetric.metricRole?.metricKind,
  );

  const mdeChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      isSuccessMetric(oldMetric) &&
      isSuccessMetric(newMetric) &&
      oldMetric.metricRole?.minimumDetectableEffect !==
        newMetric.metricRole?.minimumDetectableEffect,
  );

  const nimChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      isGuardrailMetric(oldMetric) &&
      isGuardrailMetric(newMetric) &&
      oldMetric.metricRole?.nonInferiorityMargin !==
        newMetric.metricRole?.nonInferiorityMargin,
  );

  const directionChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      oldMetric.preferredDirection !== newMetric.preferredDirection,
  );

  const oldFilters = oldValue?.filters ?? [];
  const newFilters = newValue?.filters ?? [];

  const oldFilterNames = new Set(oldFilters.map(f => f.displayName));
  const newFilterNames = new Set(newFilters.map(f => f.displayName));

  const addedFilters = newFilters.filter(
    f => !oldFilterNames.has(f.displayName),
  );
  const removedFilters = oldFilters.filter(
    f => !newFilterNames.has(f.displayName),
  );

  const scheduleChanges = updatedMetrics.filter(({ oldMetric, newMetric }) =>
    hasScheduleChanged(oldMetric, newMetric),
  );

  const surfaceOnlyChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      oldMetric.surfaceOnly !== newMetric.surfaceOnly,
  );

  const bucketChanged = oldValue?.bucket !== newValue?.bucket;
  const entityChanged = oldValue?.entity !== newValue?.entity;
  const assignmentTableChanged =
    oldValue?.assignmentTable !== newValue?.assignmentTable;
  const exposureMetricChanged =
    oldValue?.exposureMetric !== newValue?.exposureMetric;
  const exposureTableChanged =
    oldValue?.exposureTable !== newValue?.exposureTable;

  return {
    addedMetrics,
    removedMetrics,
    kindChanges,
    mdeChanges,
    nimChanges,
    directionChanges,
    addedFilters,
    removedFilters,
    scheduleChanges,
    surfaceOnlyChanges,
    bucketChanged,
    entityChanged,
    assignmentTableChanged,
    exposureMetricChanged,
    exposureTableChanged,
  };
};
